import React, { useEffect } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { observer } from 'mobx-react';
import { range } from 'lodash';

import Image from '../components/shared/Image';
import Text from '../components/shared/Text';
import { confirm } from '../components/shared/Alert';

import { warriorImageRequire } from '../lib/imagesRequire';
import { getLocalValue, t, action } from '../utils/appUtils';
import { itemCanBeBought } from '../utils/warriorUtils';
import { getBuilding } from '../utils/buildingUtils';

import appStore from '../stores/app';
import {
  PADDING1,
  CONTENT_HEIGHT,
  pOrT,
  MARGIN_10,
  BUTTON_HEIGHT,
} from '../components/styleConstants';
import Button from '../components/shared/Button';

import buildingData from '../stores/buildingData';
import { IS_IOS, IS_WEB } from '../lib/appConstants';
import heroStore from '../stores/hero';
import iapStore from '../lib/iap';

import logImport from '../lib/log';
const log = logImport as (name: string, params?: Record<string, any>) => void;

import { buildingAction } from '../lib/apiCalls';

import { BUILDING_MODULES } from '../lib/constants';
import { NameFrame } from '../components/shared/Modal';

const styles = StyleSheet.create({
  wrapBlock: {
    marginTop: MARGIN_10,
  },
  block: {
    alignItems: 'center',
    width: 170,
    height: 290,
  },
  image: {
    flex: 1,
    width: 165,
  },
});

type Avatar = {
  id: string;
  name: string;
  image: string;
  price?: number;
  iapProductId?: string;
};

type Props = {
  id: string;
};

const AvatarShop = observer(({ id }: Props) => {
  const building = getBuilding(appStore.initData.buildings, id)!;

  useEffect(() => {
    buildingData.fetch(building.id);
  }, []);

  useEffect(() => {
    if (!IS_IOS || IS_WEB || !buildingData.data) return;
    const productIds = (buildingData.data.data.avatars as Avatar[])
      .map((a) => a.iapProductId)
      .filter(Boolean) as string[];
    if (productIds.length) iapStore.init(productIds);
  }, [buildingData.data]);

  if (!buildingData.data) return null;

  const ownedAvatars = heroStore.hero.ownedAvatars || [];

  return (
    <View>
      <View style={{ padding: PADDING1 }}>
        <NameFrame style={{ top: 0 }} title={getLocalValue(building)} />
      </View>
      <ScrollView
        style={[
          { marginTop: pOrT(40, 70) },
          IS_WEB
            ? {
                height: 'calc(100vh - 330px)' as any,
              }
            : {
                height: CONTENT_HEIGHT - 26 - PADDING1 * 2,
              },
        ]}
        contentContainerStyle={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {(buildingData.data.data.avatars as Avatar[]).map((avatar) => {
          const isOwned = ownedAvatars.includes(avatar.id);
          const iapProduct =
            IS_IOS && avatar.iapProductId
              ? iapStore.getProduct(avatar.iapProductId)
              : undefined;
          const showIapButton =
            IS_IOS && !isOwned && !!avatar.iapProductId && !!iapProduct;          
          return (
            <View key={avatar.id} style={styles.wrapBlock}>
              <Image
                style={{
                  width: 165,
                  height: 290,
                  position: 'absolute',
                }}
                resizeMode="stretch"
                source={require('../assets/images/paper2.png')}
              />
              <View style={styles.block}>
                <Text
                  bold
                  style={{
                    textAlign: 'center',
                    paddingTop: PADDING1,
                    fontSize: pOrT(12, 14),
                  }}
                >
                  {getLocalValue(avatar)}
                </Text>
                <Image
                  style={styles.image}
                  resizeMode="contain"
                  source={warriorImageRequire(avatar.image)}
                />

                <View style={{ alignItems: 'center', marginBottom: PADDING1 }}>
                  {isOwned ? (
                    <Button
                      sizeObj={{ width: 120, height: BUTTON_HEIGHT }}
                      disabled={heroStore.hero.image === avatar.image}
                      onPress={() =>
                        action(async () => {
                          await buildingAction(
                            building.id,
                            (BUILDING_MODULES.AVATAR_SHOP.ACTIONS as any).APPLY,
                            { avatarId: avatar.id },
                          );
                        })
                      }
                    >
                      {t('avatarShop.apply').toUpperCase()}
                    </Button>
                  ) : (
                    <>
                      {avatar.price !== undefined && (
                        <Button
                          sizeObj={{
                            width: 120,
                            height: BUTTON_HEIGHT,
                          }}
                          disabled={!itemCanBeBought(heroStore.hero, avatar)}
                          onPress={() =>
                            action(() => {
                              confirm(
                                t('buyObjFor', {
                                  name: getLocalValue(avatar),
                                  price: avatar.price,
                                }),
                                async () => {
                                  await buildingAction(
                                    building.id,
                                    BUILDING_MODULES.AVATAR_SHOP.ACTIONS.BUY,
                                    {
                                      avatarId: avatar.id,
                                    },
                                  );
                                  appStore.showMessage({
                                    type: 'success',
                                    text: t('avatarShop.wasBought'),
                                  });
                                  log('BuyAction', {
                                    type: 'AVATAR',
                                    id: avatar.id,
                                    warriorLogin: heroStore.hero.login,
                                  });
                                },
                              );
                            })
                          }
                        >
                          {t('buyFor', { price: avatar.price }).toUpperCase()}
                        </Button>
                      )}
                      {showIapButton && (
                        <Button
                          sizeObj={{ width: 120, height: BUTTON_HEIGHT }}
                          style={{ marginTop: MARGIN_10 }}
                          disabled={iapStore.inFlight}
                          onPress={() =>
                            action(async () => {
                              try {
                                await iapStore.buy(avatar.iapProductId!, {
                                  buildingId: building.id,
                                  avatarId: avatar.id,
                                });
                                appStore.showMessage({
                                  type: 'success',
                                  text: t('avatarShop.wasBought'),
                                });
                              } catch (e) {
                                // user cancelled or validation failed; iap.ts already logged it
                              }
                            })
                          }
                        >
                          {t('avatarShop.buyWithApple', {
                            price: iapProduct!.localizedPrice,
                          }).toUpperCase()}
                        </Button>
                      )}
                    </>
                  )}
                </View>
              </View>
            </View>
          );
        })}
        {range(0, 10).map((item: number) => (
          <View key={item} style={styles.wrapBlock}>
            <View style={[styles.block, { height: 0 }]} />
          </View>
        ))}
      </ScrollView>
    </View>
  );
});

(AvatarShop as any).displayName = 'AvatarShop';

export default AvatarShop;
